import React, { useContext, useEffect, useMemo, useState } from "react";
import ThemeContext from "~/contexts/ThemeContext";
import Button from "~/components/Button/Button";
import Input from "~/components/Input/Input";
import styles from "./PromoCode.module.css";
import ModalContext from "~/contexts/ModalContext";
import SettingsContext from "~/contexts/SettingsContext";
import OrderContext from "~/contexts/OrderContext";
import { KEYS } from "~/modules/checkout/constants";
import { useFetcher, useLocation } from "@remix-run/react";
import { action } from "~/routes/$orderSource.$siteId.($table)";
import AuthContext from "~/contexts/AuthContext";
import { getCooldownMessage } from "~/helpers/promotionHelpers";
import MinusIcon from "~/icons/MinusIcon";
import { isPaymentPage } from "~/helpers/routesHelpers";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import { brandConfig } from "~/configs/brandConfigs";
import { Brand } from "~/entities/brandEntities";
import { getButtonStyles } from "~/theme/models/themeModels";
import { Shape } from "~/entities/templateEntities";

const PromoCode: React.FC = () => {
  const { template } = useContext(ThemeContext);
  const { orgSettings } = useContext(SettingsContext);
  const { setShowAssetModal } = useContext(ModalContext);
  const {
    redeemPromotionCode,
    orderSummary,
    removePromotion,
    removePromotionCode,
    promotionsRequiringSelection,
    readonlyCart,
  } = useContext(OrderContext);
  const { authenticatedUser, useComoAuth } = useContext(AuthContext);

  const hasLoyalty = !!(
    authenticatedUser?.loyaltyBalance != null &&
    authenticatedUser.loyaltyCheckbox
  );

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetcher = useFetcher<typeof action>({ key: KEYS.SUBMIT_ORDER_FETCHER });

  const promoRequiresSelection = promotionsRequiringSelection.some(
    (x) => x.allowModal
  );

  useEffect(() => {
    if (fetcher.state == "idle" && fetcher?.data?.success && submitting) {
      setSubmitting(false);
      if (promoRequiresSelection) {
        setShowAssetModal(true);
      }
    }
  }, [
    setSubmitting,
    fetcher.data,
    fetcher.state,
    submitting,
    promoRequiresSelection,
    setShowAssetModal,
  ]);

  const promoName = orgSettings?.loyaltySettings?.promotionName ?? "Promotion";

  const fetcherErrorMessage = useMemo(() => {
    const isError =
      fetcher.data && !fetcher?.data?.success && fetcher.state === "idle";

    if (!isError) return null;

    if (!useComoAuth) {
      return fetcher.data?.message || "Something went wrong.";
    }

    const cooldownMessage = getCooldownMessage(promoName);

    return cooldownMessage || fetcher.data?.message || "Something went wrong.";
  }, [fetcher.data, fetcher.state, useComoAuth, promoName]);

  const location = useLocation();
  const readOnlyLoyalty = isPaymentPage(location.pathname);

  // Styles
  const backgroundColor = template.menuScreen.general.backgroundColour;
  const fontColor = template.cart.fontColour;
  // Texts
  const haveAVoucherCode = template.loyalty.text.haveAVoucherCode;
  const usePromotion = template.loyalty.text.usePromotion;
  // Offer
  const offerIcon = template.cart.offer.icon;
  const offerBackgroundColor = template.cart.offer.backgroundColour;
  const offerFontColor = template.cart.offer.fontColour;

  // use promotion button override styles
  const usePromotionButtonStyles =
    brandConfig.Brand === Brand.BURGER_FUEL
      ? {
          ...getButtonStyles(
            "#FFFFFF",
            "#58348C",
            "#58348C",
            template.global.button.buttonSize,
            Shape.FullyRoundedBox
          ),
          "--hover-button-colour": "#FFFFFF",
          "--hover-button-border-colour": "#58348C",
          "--hover-button-font-colour": "#58348C",
          borderWidth: 2,
          fontWeight: 600,
          fontSize: 20,
        }
      : {};

  const bodyText = () => {
    return orderSummary.promoCode
      ? `Promo code: ${orderSummary.promoCode}`
      : orderSummary.redemptionName
      ? `Offer: ${orderSummary.redemptionName}`
      : "Loading...";
  };

  if (
    readOnlyLoyalty &&
    !orderSummary.redemptionKey &&
    !orderSummary.promoCode
  ) {
    return null;
  }

  const comoPromoRequiresSelection =
    !!orderSummary.redemptionKey &&
    !orderSummary.unlockedPromotions.length &&
    orderSummary.totalBeforeDiscount === orderSummary.finalPriceIncTax;

  return (orderSummary.promoCode || orderSummary.redemptionKey) &&
    !submitting ? (
    <div
      className={styles.offer}
      style={{ backgroundColor: offerBackgroundColor, color: offerFontColor }}
    >
      <div className={styles.redemptionRow}>
        <div className={styles.offerContent}>
          {offerIcon && (
            <img
              className={styles.offerIcon}
              src={uploadcareConfig.getUrl(
                offerIcon.fileId,
                offerIcon.modifiers
              )}
              alt="Offer Icon"
            />
          )}
          {comoPromoRequiresSelection || promoRequiresSelection ? (
            <button
              className={styles.promoLink}
              onClick={(e) => {
                if (readOnlyLoyalty) {
                  return;
                }

                e.preventDefault();
                e.stopPropagation();
                setShowAssetModal(true);
              }}
            >
              {bodyText()}
            </button>
          ) : (
            <b>{bodyText()}</b>
          )}
          {comoPromoRequiresSelection && (
            <span className={styles.selectionRequired}>
              (Requires Selection)
            </span>
          )}
          {promotionsRequiringSelection.some((x) => x.allowModal) ? (
            <span className={styles.selectionRequired}>
              (Selection required for{" "}
              {orderSummary.redemptionKey ? "Promotion" : "Reward"})
            </span>
          ) : (
            !!promotionsRequiringSelection.length && (
              <span className={styles.selectionRequired}>
                (Requirements not met for this{" "}
                {orderSummary.redemptionKey ? "Promotion" : "Reward"})
              </span>
            )
          )}
        </div>
        {!readOnlyLoyalty && (
          <Button
            className={styles.removeRedemptionButton}
            variant="counter-primary"
            onClick={(e) => {
              e.stopPropagation();
              orderSummary.redemptionKey
                ? removePromotion()
                : removePromotionCode();
            }}
          >
            <MinusIcon />
          </Button>
        )}
      </div>
    </div>
  ) : (
    <div
      className={styles.container}
      style={{ backgroundColor, color: fontColor }}
    >
      <div className={styles.label}>{haveAVoucherCode}</div>
      <div className={styles.inputRow}>
        <Input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter code"
        />
        <div>
          <Button
            loading={fetcher.state != "idle" && submitting}
            onClick={() => {
              setSubmitting(true);
              redeemPromotionCode(code);
            }}
            disabled={readonlyCart}
          >
            Redeem
          </Button>
        </div>
      </div>
      {authenticatedUser && (
        <Button
          variant="secondary"
          onClick={() => setShowAssetModal(true)}
          disabled={!hasLoyalty}
          style={usePromotionButtonStyles}
        >
          {usePromotion}
        </Button>
      )}
      {fetcherErrorMessage && (
        <div className={styles.errorMessage}>{fetcherErrorMessage}</div>
      )}
    </div>
  );
};

export default PromoCode;
